import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell, Section, KpiCard, StatusPill } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Save, ArrowLeft } from "lucide-react";
import { CopyField } from "@/components/loomi/CopyField";

export default function AdminVendorDetail() {
  const { vid } = useParams();
  const [v, setV] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [form, setForm] = useState({ name: "", contact_email: "", postback_url: "", revenue_share_pct: 100 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [vr, sr, dr] = await Promise.all([
      api.get(`/admin/vendors/${vid}`),
      api.get(`/admin/vendors/${vid}/sessions`),
      api.get(`/admin/vendors/${vid}/deliveries`),
    ]);
    setV(vr.data); setSessions(sr.data); setDeliveries(dr.data);
    setForm({
      name: vr.data.name || "",
      contact_email: vr.data.contact_email || "",
      postback_url: vr.data.postback_url || "",
      revenue_share_pct: vr.data.revenue_share_pct ?? 100,
    });
  };
  useEffect(() => { load(); }, [vid]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/admin/vendors/${vid}`, { ...form, revenue_share_pct: Number(form.revenue_share_pct) });
      toast.success("Vendor updated"); load();
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  if (!v) return <Shell title="Loading…" portalLabel="Admin" nav={ADMIN_NAV}><div className="text-mute">Loading vendor…</div></Shell>;
  const c = v.counters || {};

  return (
    <Shell title={v.name} portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="mb-6 text-xs text-mute">
        <Link to="/admin/vendors" className="hover:text-white inline-flex items-center gap-1" data-testid="vendor-back-link"><ArrowLeft className="w-3 h-3" /> back to vendors</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Starts" value={c.starts || 0} accent="blue" testId="vd-kpi-starts" />
        <KpiCard label="Completes" value={c.complete || 0} accent="green" testId="vd-kpi-completes" />
        <KpiCard label="Terminates" value={c.terminate || 0} accent="red" testId="vd-kpi-terminates" />
        <KpiCard label="Overquotas" value={c.overquota || 0} accent="amber" testId="vd-kpi-overquotas" />
        <KpiCard label="Rev share" value={`${v.revenue_share_pct}%`} accent="primary" testId="vd-kpi-revshare" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <form onSubmit={save} className="glass rounded-2xl p-6" data-testid="vendor-config-form">
          <h3 className="font-display font-bold text-lg mb-4">Configuration</h3>
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Name</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vd-name" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-3 text-sm" />
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Contact email</label>
          <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="vd-email" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-3 text-sm" />
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Postback URL</label>
          <input type="url" value={form.postback_url} onChange={(e) => setForm({ ...form, postback_url: e.target.value })} data-testid="vd-postback" placeholder="https://vendor.example.com/postback" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-3 text-sm font-mono" />
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Revenue share %</label>
          <input type="number" min="0" max="100" value={form.revenue_share_pct} onChange={(e) => setForm({ ...form, revenue_share_pct: e.target.value })} data-testid="vd-revshare" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 mb-4 text-sm" />
          <button disabled={saving} data-testid="vd-save" className="bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 rounded-md py-2 px-4 text-sm font-medium inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </form>

        <Section title="Vendor identifier" testId="section-vendor-id">
          <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Vendor ID (share with vendor for their vid= parameter)</div>
          <CopyField value={v.id} testId="vd-vendor-id" />
          <div className="text-[10px] uppercase tracking-widest text-mute mt-4 mb-1">Last 4 of API key</div>
          <div className="font-mono text-xs bg-black/50 border border-white/10 rounded-md px-3 py-2">loomi_sk_…{v.api_key_last4}</div>
          <div className="mt-4 text-xs text-mute">To rotate the API key or view a new webhook secret, use the <Link className="text-indigo-300 hover:underline" to="/admin/vendors" data-testid="vd-goto-list">vendors list</Link> and click Rotate.</div>
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Section title="Recent vendor sessions" testId="section-vendor-sessions">
          {sessions.length === 0 ? <div className="text-mute text-sm py-6 text-center" data-testid="vd-sessions-empty">No respondent traffic yet.</div> : (
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="vd-sessions-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-mute border-b border-white/10">
                    <th className="py-2 pr-3">Sub ID</th><th className="py-2 pr-3">RID</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Entry</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-b border-white/5">
                      <td className="py-2 pr-3 font-mono">{s.vendor_sub_id || "—"}</td>
                      <td className="py-2 pr-3 font-mono">{s.rid_token}</td>
                      <td className="py-2 pr-3"><StatusPill status={s.status} /></td>
                      <td className="py-2 pr-3 text-mute">{new Date(s.entry_time).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
        <Section title="Postback deliveries" testId="section-vendor-deliveries">
          {deliveries.length === 0 ? <div className="text-mute text-sm py-6 text-center" data-testid="vd-deliveries-empty">No postbacks dispatched. Set a postback URL above and run a test respondent.</div> : (
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="vd-deliveries-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-mute border-b border-white/10">
                    <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Event</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">HTTP</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id} className="border-b border-white/5">
                      <td className="py-2 pr-3 font-mono text-mute">{new Date(d.timestamp).toLocaleTimeString()}</td>
                      <td className="py-2 pr-3 font-mono">{d.event}</td>
                      <td className={`py-2 pr-3 font-mono ${d.ok ? "status-complete" : "status-terminate"}`}>{d.ok ? "ok" : "fail"}</td>
                      <td className="py-2 pr-3 font-mono">{d.response_status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </Shell>
  );
}
